import type { MocXuatBan, TheChiSo } from '@qlk/shared';
import { useEffect, useState, type ReactNode } from 'react';

import { Skeleton } from '../../../../ui/index.js';
import { BieuDoKy, type MauBieuDo } from './BieuDoKy.js';
import { HangThe } from './TheChiSo.js';

/**
 * Khối "hàng thẻ + biểu đồ": bấm thẻ nào thì biểu đồ bên dưới vẽ chỉ số đó.
 *
 * Dùng ở hai nơi, và hai nơi khác nhau đúng hai chỗ:
 *
 * - **bốn tab trang kênh** để khung tự giữ thẻ đang chọn và tự vẽ `BieuDoKy`
 *   từ `the.diem`;
 * - **trang Hiệu suất** truyền `chon`/`onChon` (thẻ đang chọn nằm trong
 *   `?chi-so=` của URL) và truyền `bieuDo` riêng, vì biểu đồ ở đó là một
 *   request khác theo chỉ số đang chọn và có thêm chuỗi kỳ trước.
 *
 * Giữ một khung cho cả hai thay vì dựng hàng thẻ thứ hai: cỡ số, cách xuống
 * hàng và dòng "so với …" của hai màn phải giống nhau, mà đó đúng là phần dễ
 * trôi khỏi nhau nhất nếu có hai bản.
 */

export interface KhungChiSoProps {
  the: TheChiSo[];
  mocXuatBan: MocXuatBan[];
  mau: MauBieuDo;
  nhanKy?: string;
  /** Khoảng của kỳ trước, cho dòng so sánh của từng thẻ. */
  nhanKyTruoc?: string;
  dangTai: boolean;
  /**
   * Thẻ đang chọn, khi chỗ gọi tự giữ (trang Hiệu suất giữ nó trong URL).
   * Bỏ trống thì khung tự giữ như cũ.
   */
  chon?: string;
  onChon?: (khoa: string) => void;
  /**
   * Biểu đồ thay cho `BieuDoKy` mặc định.
   *
   * Khi có, nút "Xem thêm" cũng ẩn: bảng số từng ngày là của `BieuDoKy`, biểu
   * đồ thay thế tự lo phần trình đọc màn hình của nó.
   */
  bieuDo?: ReactNode;
  /** Hình dáng ô — xem `HangTheProps.kieu`. */
  kieu?: 'the' | 'o';
}

export function KhungChiSo({
  the,
  mocXuatBan,
  mau,
  nhanKy,
  nhanKyTruoc,
  dangTai,
  chon,
  onChon,
  bieuDo,
  kieu,
}: KhungChiSoProps) {
  const [chonTrong, datChonTrong] = useState('');
  const [moBang, datMoBang] = useState(false);

  const ngoaiGiu = chon !== undefined;
  const dangChonTho = ngoaiGiu ? chon : chonTrong;

  /* Đổi kỳ hoặc đổi tab thì danh sách thẻ đổi — về thẻ đầu. Chỉ khi khung tự
     giữ: chỗ gọi nào tự giữ thì tự lo mã lạ (xem `chiSoTuUrl`). */
  useEffect(() => {
    if (ngoaiGiu) return;
    const dau = the[0]?.khoa;
    if (dau !== undefined && !the.some((t) => t.khoa === chonTrong)) datChonTrong(dau);
  }, [the, chonTrong, ngoaiGiu]);

  if (dangTai) {
    return (
      <div className="space-y-px">
        <Skeleton className="h-[108px] w-full rounded-t-the" />
        <Skeleton className="h-[272px] w-full rounded-b-the" />
      </div>
    );
  }

  if (the.length === 0) {
    return (
      <div className="rounded-the border border-vien bg-be-mat p-6 text-co-ban text-chu-mo">
        Chưa có số liệu trong khoảng này.
      </div>
    );
  }

  const dangXem = the.find((t) => t.khoa === dangChonTho) ?? the[0];
  if (!dangXem) return null;

  return (
    <div>
      <HangThe
        the={the}
        dangChon={dangXem.khoa}
        onChon={onChon ?? datChonTrong}
        {...(nhanKyTruoc === undefined ? {} : { nhanKyTruoc })}
        {...(kieu === undefined ? {} : { kieu })}
      />

      {bieuDo ?? (
        <BieuDoKy the={dangXem} mocXuatBan={mocXuatBan} mau={mau} nhanKy={nhanKy} moBang={moBang} />
      )}

      {/* "Xem thêm" mở bảng số từng ngày của chính chỉ số đang xem. */}
      {bieuDo === undefined && (
        <div className="mt-3">
          <button
            type="button"
            aria-expanded={moBang}
            onClick={() => {
              datMoBang((x) => !x);
            }}
            className="vung-bam rounded-full border border-vien bg-be-mat px-4 text-co-ban font-semibold text-nhan hover:bg-neutral-nen"
          >
            {moBang ? 'Thu gọn' : 'Xem thêm'}
          </button>
        </div>
      )}
    </div>
  );
}
